import { ScaleElement } from '@scale/base-element';

const BUTTON_GAP = 8;
const CONTAINER_GAP = 10;

class ScaleHotspot extends ScaleElement {
  static requiredRefs = ['trigger', 'dialog'];

  #hoverTimer = null;
  #dismissController = null;

  get #useModal() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || this.isMobile;
  }

  setup() {

    this.on('click', () => this.#handleClick(), { target: this.ref('trigger') });

    if (!this.#useModal) {
      this.on('pointerenter', () => this.#handlePointerEnter(), { target: this.ref('trigger') });
      this.on('pointerleave', () => this.#handlePointerLeave());
    }

    this.mediaQuery('(width >= 750px)', (matches) => {
      if (!matches) this.#close();
    });
  }

  teardown() {
    clearTimeout(this.#hoverTimer);
    this.#dismissController?.abort();
  }

  #handleClick() {
    const dialog = this.ref('dialog');
    if (dialog.open) {
      this.#close();
      return;
    }

    if (this.#useModal) {
      this.#openModal();
    } else {
      this.#openPopover();
    }
  }

  #handlePointerEnter() {
    if (this.#useModal) return;
    const dialog = this.ref('dialog');
    if (dialog.open) return;

    this.#hoverTimer = setTimeout(() => this.#openPopover(), 120);
  }

  #handlePointerLeave() {
    clearTimeout(this.#hoverTimer);

    const dialog = this.ref('dialog');
    if (!dialog.open) return;

    setTimeout(() => {
      if (!this.matches(':hover') && !dialog.matches(':hover')) {
        this.#close();
      }
    }, 80);
  }

  #openPopover() {
    this.#closeOthers();
    const dialog = this.ref('dialog');
    this.#calculatePlacement();
    dialog.show();
    this.ref('trigger').setAttribute('aria-expanded', 'true');
    this.#addDismissListeners();
  }

  #openModal() {
    this.#closeOthers();
    const dialog = this.ref('dialog');
    dialog.showModal();
    this.ref('trigger').setAttribute('aria-expanded', 'true');
    this.#addDismissListeners();
  }

  #addDismissListeners() {
    this.#dismissController?.abort();
    this.#dismissController = new AbortController();
    const { signal } = this.#dismissController;

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target) && !this.ref('dialog').contains(e.target)) {
        this.#close();
      }
    }, { signal });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.#close();
    }, { signal });

    this.ref('dialog').addEventListener('click', (e) => {
      if (e.target === this.ref('dialog')) this.#close();
    }, { signal });
  }

  #close() {
    this.#dismissController?.abort();
    this.#dismissController = null;

    const dialog = this.ref('dialog');
    if (!dialog.open) return;
    dialog.close();
    this.ref('trigger').setAttribute('aria-expanded', 'false');
    dialog.removeAttribute('data-placement');
    dialog.style.removeProperty('--dialog-offset-y');
  }

  #closeOthers() {
    document.querySelectorAll('scale-hotspot').forEach((el) => {
      if (el !== this) el.closeDialog();
    });
  }

  closeDialog() {
    this.#close();
  }

  #calculatePlacement() {
    const trigger = this.ref('trigger');
    const dialog = this.ref('dialog');
    const container = this.closest('.image-hotspot__pins');
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.removeAttribute('data-placement');
    const dialogRect = dialog.getBoundingClientRect();
    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    let x = 'right';
    if (spaceRight >= dialogRect.width + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogRect.width + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    let y = 'bottom';
    let offsetY = 0;

    if (x !== 'center') {
      const dialogEndY = buttonTop + dialogRect.height;
      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        y = 'top';
        const dialogStartY = buttonBottom - dialogRect.height;
        if (dialogStartY < CONTAINER_GAP) {
          offsetY = CONTAINER_GAP - dialogStartY;
        }
      } else if (buttonTop < CONTAINER_GAP) {
        offsetY = CONTAINER_GAP - buttonTop;
      }
    } else {
      const spaceBelow = containerRect.height - buttonBottom;
      y = spaceBelow >= dialogRect.height + BUTTON_GAP + CONTAINER_GAP ? 'bottom' : 'top';
    }

    dialog.dataset.placement = `${x},${y}`;
    if (offsetY !== 0) {
      dialog.style.setProperty('--dialog-offset-y', `${offsetY}px`);
    }
  }

  onBlockSelect() {
    if (this.#useModal) {
      this.#openModal();
    } else {
      this.#openPopover();
    }
  }

  onBlockDeselect() {
    this.#close();
  }
}

if (!customElements.get('scale-hotspot')) {
  customElements.define('scale-hotspot', ScaleHotspot);
}
